import styled from "styled-components";

import { colorsPalettes, rgba, setFontVariant } from "@similarweb/styles";
import { PopupHoverContainer, PixelPlaceholderLoader } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";
import type { DifficultyLevel, DifficultyLevelLimits } from "app/common/difficulty/difficulty.utils";
import {
    DIFFICULTY_LEVEL_WITH_LIMITS,
    MAP_DIFFICULTY_LEVEL_TO_BACKGROUND_COLOR,
    getDifficultyLevel,
} from "app/common/difficulty/difficulty.utils";
import { DifficultyBar } from "app/pages/digital-marketing/difficulty-bar/DifficultyBar";
import { getDifficultyBarTexts } from "app/pages/digital-marketing/difficulty-bar/DifficultyBar.utils";
import { CenteredFlexRow, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const NO_DATA_MESSAGE = "-";
const i18n = i18nFilter();

const KeywordDifficultyCellWrapper = styled(CenteredFlexRow)`
    gap: 9px;
`;

const TextStyled = styled.span`
    ${setFontVariant("body2", rgba(colorsPalettes.carbon[500], 0.8))};
`;

const CompareDivider = styled(TextStyled)`
    margin: 0 10px;
`;

const SecondaryTextStyled = styled.span`
    ${setFontVariant("body2", colorsPalettes.carbon[300])};
`;

const Circle = styled.div<{ background: string; size: string }>`
    background: ${({ background }) => background};
    border-radius: 50%;
    width: ${({ size }) => size};
    height: ${({ size }) => size};
`;

const getCircleProps = (difficultyGrade: number) => ({
    size: "10px",
    background:
        // @ts-ignore FIXME: strict migration - 'undefined' cannot be used as index — getDifficultyLevel may return undefined for out-of-range grades
        MAP_DIFFICULTY_LEVEL_TO_BACKGROUND_COLOR[getDifficultyLevel(difficultyGrade, DIFFICULTY_LEVEL_WITH_LIMITS)],
});

type KeywordDifficultyCellInnerProps = { difficultyGrade: number };

const KeywordDifficultyCellContent = ({ difficultyGrade }: KeywordDifficultyCellInnerProps) => {
    return (
        <KeywordDifficultyCellWrapper className="kw-difficulty-value">
            <Circle {...getCircleProps(difficultyGrade)} />
            <div>{difficultyGrade}</div>
        </KeywordDifficultyCellWrapper>
    );
};

type KeywordDifficultyCellInnerWithLabelProps = KeywordDifficultyCellInnerProps & {
    difficultyLevelsWithLimits?: Record<DifficultyLevel, DifficultyLevelLimits>;
};
const KeywordDifficultyCellContentWithLabel = ({
    difficultyGrade,
    difficultyLevelsWithLimits = DIFFICULTY_LEVEL_WITH_LIMITS,
}: KeywordDifficultyCellInnerWithLabelProps) => {
    const difficultyLevel = getDifficultyLevel(difficultyGrade, difficultyLevelsWithLimits);
    // @ts-ignore FIXME: strict migration - 'DifficultyLevel | undefined' not assignable to 'DifficultyLevel' — out-of-range grades return undefined
    const { labelText } = getDifficultyBarTexts(i18n, difficultyLevel, "");
    return (
        <KeywordDifficultyCellWrapper className="kw-difficulty-value">
            <Circle {...getCircleProps(difficultyGrade)} />
            <TextStyled>{difficultyGrade}</TextStyled>
            <SecondaryTextStyled>({labelText})</SecondaryTextStyled>
        </KeywordDifficultyCellWrapper>
    );
};

type KeywordDifficultyCellInnerWithLabelCompareProps = {
    minDifficultyGrade: number;
    maxDifficultyGrade: number;
    difficultyLevelsWithLimits?: Record<DifficultyLevel, DifficultyLevelLimits>;
};
const KeywordDifficultyCellContentWithLabelCompare = ({
    minDifficultyGrade,
    maxDifficultyGrade,
    difficultyLevelsWithLimits = DIFFICULTY_LEVEL_WITH_LIMITS,
}: KeywordDifficultyCellInnerWithLabelCompareProps) => {
    return (
        <>
            <KeywordDifficultyCellContentWithLabel
                difficultyGrade={minDifficultyGrade}
                difficultyLevelsWithLimits={difficultyLevelsWithLimits}
            />
            {minDifficultyGrade !== maxDifficultyGrade ? (
                <>
                    <CompareDivider>-</CompareDivider>
                    <KeywordDifficultyCellContentWithLabel
                        difficultyGrade={maxDifficultyGrade}
                        difficultyLevelsWithLimits={difficultyLevelsWithLimits}
                    />
                </>
            ) : null}
        </>
    );
};

const DifficultyBarWrapper = styled.div`
    width: 224px;
`;

export type KeywordDifficultyCellProps = {
    value: number;
    row: {
        countryName: string;
        serpLoading: boolean;
        getDifficultyBarTexts?: typeof getDifficultyBarTexts;
    };
};

const WrapWithKeywordDifficultyTooltip = ({
    difficultyGrade,
    countryName,
    children,
    getDifficultyBarTexts = undefined,
}: FixmeType) => {
    return (
        <PopupHoverContainer
            content={() => (
                <DifficultyBarWrapper>
                    <DifficultyBar
                        getDifficultyBarTexts={getDifficultyBarTexts}
                        difficultyGrade={difficultyGrade}
                        country={countryName}
                    />
                </DifficultyBarWrapper>
            )}
            config={{ placement: "right" }}
        >
            {children}
        </PopupHoverContainer>
    );
};

export const KeywordDifficultyCell = ({ value, row }: KeywordDifficultyCellProps) => {
    if (row.serpLoading) {
        return (
            <PixelPlaceholderLoader
                width={40}
                height={16}
            />
        );
    }
    const shouldDisplayNoDataMessage = !value;
    if (shouldDisplayNoDataMessage) {
        return <div>{NO_DATA_MESSAGE}</div>;
    }
    return (
        <WrapWithKeywordDifficultyTooltip
            getDifficultyBarTexts={row.getDifficultyBarTexts}
            difficultyGrade={value}
            countryName={row.countryName}
        >
            <FlexRow
                justifyContent="flex-start"
                alignItems="center"
            >
                <KeywordDifficultyCellContent difficultyGrade={value} />
            </FlexRow>
        </WrapWithKeywordDifficultyTooltip>
    );
};
